import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Callout } from '@hvcgroep/design-system';
import Accordion from '../../../../shared/components/Accordion';
import { RegistrationDeskEventHandler } from '../../state-machine';
import Button from '../../../../shared/components/Button';
import AdjustUsageSlider from '../../containers/AdjustUsageSlider';
import OfferBlock from '../../containers/OfferBlock';
import AddressSelectionInfo from '../../../../shared/containers/AddressSelection/Info';
import HouseTypeBlock from '../../containers/HouseTypeBlock';
import GJToM3Comparison from '../../containers/GJToM3Comparison';
import { RegistrationDeskStepComponent } from '../../types/multistep-form';

import './style.scss';
import { gjtom3 } from '../../helpers/gasCalculation';

interface Props {
  onUsageUpdate: RegistrationDeskEventHandler<'proceed.updateUsage'>;
  onAddressChange: RegistrationDeskEventHandler<'reset.changeDeliveryAddress'>;
  onOpenEstimateExplanation: RegistrationDeskEventHandler<'info.viewEstimateExplanation'>;
  onOpenPricingStructure: RegistrationDeskEventHandler<'info.viewPricingStructure'>;
  onOpenCustomUsageAccordion: RegistrationDeskEventHandler<'openCustomUsageAccordion'>;
  onCloseCustomUsageAccordion: RegistrationDeskEventHandler<'closeCustomUsageAccordion'>;
}

const AdjustUsageScreen: RegistrationDeskStepComponent<'proceed.saveUsage', Props> = (props) => {
  const {
    context,
    onUsageUpdate,
    onAddressChange,
    onProceed,
    onOpenEstimateExplanation,
    onOpenPricingStructure,
    onOpenCustomUsageAccordion,
    onCloseCustomUsageAccordion,
  } = props;

  const eamData = context.eamData!;
  const registrationType = context.registrationType!;
  const sjvM3 = String(context.usageM3!);
  const sjvGJ = String(context.usageGJ!);

  const { t } = useTranslation();

  return (
    <div>
      <AddressSelectionInfo
        address={eamData}
        onAddressReset={() => onAddressChange({})}
        addressMapper={{
          house_number: 'Housenumber',
          house_number_extension: 'HousenumberExt',
          city: 'City',
          postal_code: 'PostalCode',
          street: 'Street',
        }}
      />
      <p>
        {registrationType === 'moving' && (
          <>
            {t('registration_desk.screens.adjust_usage.moving.body')}{' '}
            <button
              type="button"
              onClick={() => onOpenEstimateExplanation({})}
              className="a no-styling"
            >{t('registration_desk.screens.adjust_usage.link')}
            </button>
          </>
        )}
        {registrationType === 'renovation' && t('registration_desk.screens.adjust_usage.renovation.body')}
      </p>
      <h3 className="AdjustUsage__title">{t('registration_desk.offer_block.title')}</h3>
      <OfferBlock
        sjvGJ={sjvGJ}
        ean={eamData.Ean}
        onOpenPricingStructure={() => onOpenPricingStructure({})}
        disableLottieReveal={context.seenSteps.has('/verbruik-aanpassen')}
      />
      <Accordion
        id="adjust-usage-slider"
        title="verbruik aanpassen"
        onOpen={() => {
          onOpenCustomUsageAccordion({});
        }}
        onClose={() => {
          onCloseCustomUsageAccordion({});
        }}
        isInitiallyOpen={context.isCustomUsageAccordionOpened}
        content={(
          <div className="faq-item__inner-component">
            <AdjustUsageSlider
              registrationType={registrationType}
              onUpdate={onUsageUpdate}
              sjvM3={sjvM3}
              sjvGJ={sjvGJ}
              ean={eamData.Ean}
            />
            {registrationType === 'renovation' && (
              <GJToM3Comparison standardAnnualUsageInM3={sjvM3} standardAnnualUsageInGJ={sjvGJ} />
            )}
            <HouseTypeBlock
              sjvGJ={sjvGJ}
              className="mt-5"
              onOpenEstimateExplanation={() => onOpenEstimateExplanation({})}
            />
          </div>
        )}
      />
      {eamData.SjvTot !== sjvGJ && (
        <Callout className="mt-5">
          <strong>{t('registration_desk.screens.adjust_usage.callout_label')}</strong>
          <p className="mb-0">{t('registration_desk.screens.adjust_usage.callout_sjv')}</p>
          <button
            type="button"
            className="a no-styling"
            onClick={() => {
              onUsageUpdate({
                usageM3: String(gjtom3(Number(eamData.SjvTot))),
              });
            }}
          >
            {t('registration_desk.screens.adjust_usage.callout_reset')}
          </button>
        </Callout>
      )}
      <div className="d-flex">
        <Button
          className="mt-5 ml-auto"
          type="secondary"
          variation="center"
          onClick={() => onProceed({
            usageM3: sjvM3,
          })}
        >
          {t('registration_desk.buttons.next')}
        </Button>
      </div>
    </div>
  );
};

export default AdjustUsageScreen;
